import { useTranslation } from 'react-i18next'
import { useContactProfileQuery } from '@/queries/conversations'

/** "Local time" of someone whose clock is `offset` hours from UTC. */
function localTime(offset: number): string {
  return new Date(Date.now() + offset * 3_600_000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  })
}

function languageName(locale: string, uiLanguage: string): string {
  // Meta writes Latin American Spanish as es_LA; for the browser LA is Laos.
  const tag = locale.replace(/_LA$/, '-419').replace('_', '-')
  try {
    return new Intl.DisplayNames([uiLanguage], { type: 'language' }).of(tag) ?? locale
  } catch {
    return locale
  }
}

/**
 * Language, local time and gender of a Messenger contact, read from Meta
 * (`pages_user_locale`, `pages_user_timezone`, `pages_user_gender`): which
 * language to answer in, and whether it is a good hour to write.
 */
export function MessengerContactDetails({ conversationId, platform }: { conversationId: string; platform: string }) {
  const { t, i18n } = useTranslation()
  const isMessenger = platform === 'facebook'
  const profile = useContactProfileQuery(conversationId, isMessenger)
  const data = profile.data
  if (!isMessenger || !data?.supported || data.error) return null

  const offset = data.timezone
  return (
    <div className="space-y-1 text-sm text-muted-foreground">
      {data.locale && (
        <p>{t('contact_profile.language')}: {languageName(data.locale, i18n.language)} ({data.locale})</p>
      )}
      {typeof offset === 'number' && (
        <p>
          {t('contact_profile.local_time')}: {localTime(offset)} ({t('contact_profile.utc', { offset: offset >= 0 ? `+${offset}` : offset })})
        </p>
      )}
      {data.gender && (
        <p>{t('contact_profile.gender')}: {t(`contact_profile.genders.${data.gender}`, { defaultValue: data.gender })}</p>
      )}
    </div>
  )
}
